import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import * as projects from 'shared/api/projects';
import * as creativeAssets from 'shared/api/creativeAssets';
import type * as fetch from 'shared/api/fetch';
import { HttpStatusCodes } from 'shared/httpStatus';
import { retryDelayFn, retryFn } from 'shared/reactQuery';
import { splitEvery } from 'ramda';
import { delayBetweenBatches } from 'shared/utils';
import { BULK_UPDATE_BATCH_SIZE } from 'src/creativeAssets/constants';
export const queryKeys = {
  projects: 'projects',
  resources: 'resources',
  creativeAssets: 'creative-assets',
  creativeMetadata: 'creative-metadata',
  projectMetadata: 'project-metadata',
  versions: 'versions',
  requestedChanges: 'requested-changes'
};
export const getCreativeMetadataQueryKey = (clientId: number, projectId: string, creativeId: string): readonly [typeof queryKeys.creativeMetadata, number, string, string] => [queryKeys.creativeMetadata, clientId, projectId, creativeId];
export const fetchCreativeMetadata = async (args: {
  clientId: number;
  projectId: string;
  creativeId: string;
}): Promise<creativeAssets.CreativeMetadata | null> => {
  try {
    return await creativeAssets.getCreativeMetadata(args);
  } catch (error) {
    if ((error as fetch.ClientError).status_code === HttpStatusCodes.NOT_FOUND) {
      return null;
    }
    throw error;
  }
};
export const useMutationProject = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: projects.createProject,
      onSuccess: async createdProject => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projects, createdProject.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useQueryProjects = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.projects, clientId];
    t1 = async () => await projects.getProjects(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryProject = (clientId, projectId) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== clientId || $[1] !== projectId) {
    t0 = [queryKeys.projects, clientId, projectId];
    t1 = async () => await projects.getProject(clientId, projectId);
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = Boolean(clientId) && projectId !== "";
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useMutationUpdateProject = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: async t1 => {
        const {
          clientId: clientId_0,
          id: projectId_0
        } = t1;
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projects, clientId_0, projectId_0.toString()]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projects, clientId_0]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteProject = clientId => {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== clientId) {
    t0 = async t1 => {
      const {
        projectId
      } = t1;
      return await projects.deleteProject(clientId, projectId);
    };
    $[0] = clientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== clientId || $[3] !== queryClient) {
    t1 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.projects, clientId]
      });
    };
    $[2] = clientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
};
export const useMutationCreateResource = (projectId, clientId) => {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== projectId) {
    t0 = async data => await projects.createResource(projectId, data);
    $[0] = projectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== clientId || $[3] !== projectId || $[4] !== queryClient) {
    t1 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.resources, clientId, projectId]
      });
    };
    $[2] = clientId;
    $[3] = projectId;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useMutation(t2);
};
export const useQueryResources = (projectId, clientId) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== clientId || $[1] !== projectId) {
    t0 = [queryKeys.resources, clientId, projectId];
    t1 = async () => await projects.getResources(projectId, clientId);
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = projectId !== "" && Boolean(clientId);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useMutationDeleteResource = (projectId, clientId) => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== clientId || $[1] !== projectId) {
    t0 = async t1 => {
      const {
        resourceId
      } = t1;
      await projects.deleteResource(projectId, resourceId, clientId);
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== clientId || $[4] !== projectId || $[5] !== queryClient) {
    t1 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.resources, clientId, projectId]
      });
    };
    $[3] = clientId;
    $[4] = projectId;
    $[5] = queryClient;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
export const useMutationCreateBatchId = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: creativeAssets.createBatchId
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationCreateSignedUrls = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: creativeAssets.createSignedUrls,
      retry: retryFn(2),
      retryDelay: retryDelayFn
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationUploadFile = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: creativeAssets.uploadFile
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMutationCreateCreative = (clientId, projectId) => {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = retryFn(2);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== clientId || $[2] !== projectId || $[3] !== queryClient) {
    t1 = {
      mutationFn: creativeAssets.createCreative,
      retry: t0,
      retryDelay: retryDelayFn,
      onSuccess: async creative => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeAssets, clientId, projectId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.versions, clientId, projectId, creative.id]
        });
      }
    };
    $[1] = clientId;
    $[2] = projectId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useQueryCreatives = t0 => {
  const $ = _c(8);
  const {
    projectId,
    clientId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== projectId) {
    t1 = [queryKeys.creativeAssets, clientId, projectId];
    t2 = async () => await creativeAssets.getCreatives({
      projectId,
      clientId
    });
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = projectId !== "" && Boolean(clientId);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useQueryCreative = t0 => {
  const $ = _c(10);
  const {
    clientId,
    projectId,
    creativeId,
    legacyFolderId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== clientId || $[1] !== creativeId || $[2] !== legacyFolderId || $[3] !== projectId) {
    t2 = [queryKeys.creativeAssets, clientId, projectId, creativeId, legacyFolderId];
    t3 = async () => await creativeAssets.getCreative({
      clientId,
      projectId,
      creativeId,
      legacyFolderId
    });
    $[0] = clientId;
    $[1] = creativeId;
    $[2] = legacyFolderId;
    $[3] = projectId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  const t4 = enabled && Boolean(clientId) && projectId !== "" && creativeId !== "";
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useQuery(t5);
};
export const useQueryCreativeMetadata = t0 => {
  const $ = _c(12);
  const {
    clientId,
    projectId,
    creativeId
  } = t0;
  let t1;
  if ($[0] !== clientId || $[1] !== creativeId || $[2] !== projectId) {
    t1 = getCreativeMetadataQueryKey(clientId, projectId, creativeId);
    $[0] = clientId;
    $[1] = creativeId;
    $[2] = projectId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== clientId || $[5] !== creativeId || $[6] !== projectId) {
    t2 = async () => await fetchCreativeMetadata({
      clientId,
      projectId,
      creativeId
    });
    $[4] = clientId;
    $[5] = creativeId;
    $[6] = projectId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = Boolean(clientId) && projectId !== "" && creativeId !== "";
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      throwOnError: false
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
};
export const useQueryCreativesMetadata = t0 => {
  const $ = _c(8);
  const {
    clientId,
    projectId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== projectId) {
    t1 = [queryKeys.creativeMetadata, clientId, projectId];
    t2 = async () => await creativeAssets.getCreativesMetadata({
      clientId,
      projectId
    });
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && projectId !== "";
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useMutationCreateOrUpdateCreativeMetadata = (clientId, projectId) => {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = retryFn(2);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== clientId || $[2] !== projectId || $[3] !== queryClient) {
    t1 = {
      mutationFn: creativeAssets.createOrUpdateCreativeMetadata,
      retry: t0,
      retryDelay: retryDelayFn,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeMetadata, clientId, projectId]
        });
      }
    };
    $[1] = clientId;
    $[2] = projectId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useMutationDeleteCreative = (clientId, projectId) => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== clientId || $[1] !== projectId) {
    t0 = async t1 => {
      const {
        creativeId,
        legacyFolderId
      } = t1;
      await creativeAssets.deleteCreative({
        clientId,
        projectId,
        creativeId,
        legacyFolderId
      });
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== clientId || $[4] !== projectId || $[5] !== queryClient) {
    t1 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.creativeAssets, clientId, projectId]
      });
    };
    $[3] = clientId;
    $[4] = projectId;
    $[5] = queryClient;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
export const useMutationDeleteCreativeMetadata = (clientId, projectId) => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== clientId || $[1] !== projectId) {
    t0 = async t1 => {
      const {
        creativeId
      } = t1;
      await creativeAssets.deleteCreativeMetadata({
        clientId,
        projectId,
        creativeId
      });
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== clientId || $[4] !== projectId || $[5] !== queryClient) {
    t1 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.creativeAssets, clientId, projectId]
      });
    };
    $[3] = clientId;
    $[4] = projectId;
    $[5] = queryClient;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
type BulkDeleteConfigResult = Array<{
  creativeId: string;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useBulkMutationDeleteCreatives = t0 => {
  const $ = _c(10);
  const {
    clientId,
    projectId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== projectId) {
    t1 = async t2 => {
      const {
        creatives
      } = t2;
      const results = [];
      for (const [index, batch] of splitEvery(BULK_UPDATE_BATCH_SIZE, creatives).entries()) {
        if (index > 0) {
          await delayBetweenBatches();
        }
        const batchResults = await Promise.all(batch.map(async t3 => {
          const {
            id: creativeId,
            legacyFolderId
          } = t3;
          ;
          try {
            await creativeAssets.deleteCreative({
              clientId,
              projectId,
              creativeId,
              legacyFolderId
            });
            await creativeAssets.deleteCreativeMetadata({
              clientId,
              projectId,
              creativeId
            });
            return {
              creativeId,
              success: true
            };
          } catch (t4) {
            const error = t4;
            return {
              creativeId,
              success: false,
              error: error as fetch.ClientError
            };
          }
        }));
        results.push(...batchResults);
      }
      return results;
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== clientId || $[4] !== projectId || $[5] !== queryClient) {
    t2 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.creativeAssets, clientId, projectId]
      });
    };
    $[3] = clientId;
    $[4] = projectId;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
};
export const useMutationCreateProjectMetadata = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: creativeAssets.createProjectMetadata,
      onSuccess: async data => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projectMetadata, data.projectId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useQueryProjectMetadata = t0 => {
  const $ = _c(8);
  const {
    projectId,
    clientId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== projectId) {
    t1 = [queryKeys.projectMetadata, projectId, clientId];
    t2 = async () => await creativeAssets.getProjectMetadata({
      projectId,
      clientId
    });
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = projectId !== "" && Boolean(clientId);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      throwOnError: false
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useMutationUpdateProjectMetadata = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: creativeAssets.updateProjectMetadata,
      onSuccess: async data => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projectMetadata, data.projectId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteProjectMetadata = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.projectMetadata, variables.projectId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDownloadFile = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: creativeAssets.downloadFile
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useQueryVersionHistory = t0 => {
  const $ = _c(10);
  const {
    clientId,
    projectId,
    creativeId,
    legacyFolderId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== clientId || $[1] !== creativeId || $[2] !== legacyFolderId || $[3] !== projectId) {
    t2 = [queryKeys.versions, clientId, projectId, creativeId, legacyFolderId];
    t3 = async () => await creativeAssets.getVersions({
      clientId,
      projectId,
      creativeId,
      legacyFolderId
    });
    $[0] = clientId;
    $[1] = creativeId;
    $[2] = legacyFolderId;
    $[3] = projectId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  const t4 = enabled && Boolean(clientId) && projectId !== "" && creativeId !== "";
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useQuery(t5);
};
export const useQueryRequestedChanges = t0 => {
  const $ = _c(9);
  const {
    clientId,
    projectId,
    creativeId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== creativeId || $[2] !== projectId) {
    t1 = [queryKeys.requestedChanges, clientId, projectId, creativeId];
    t2 = async () => await creativeAssets.getRequestedChanges({
      clientId,
      projectId,
      creativeId
    });
    $[0] = clientId;
    $[1] = creativeId;
    $[2] = projectId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = Boolean(clientId) && projectId !== "" && creativeId !== "";
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
};
export const useMutationCreateRequestedChange = t0 => {
  const $ = _c(4);
  const {
    clientId,
    projectId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== projectId || $[2] !== queryClient) {
    t1 = {
      mutationFn: creativeAssets.createRequestedChange,
      onSuccess: async (_data, variables) => {
        const {
          creativeId
        } = variables;
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.requestedChanges, clientId, projectId, creativeId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeMetadata, clientId, projectId]
        });
      }
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useMutation(t1);
};
export const useQueryRequestedChange = t0 => {
  const $ = _c(8);
  const {
    clientId,
    changeId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== changeId || $[1] !== clientId) {
    t1 = [queryKeys.requestedChanges, clientId, changeId];
    t2 = async () => await creativeAssets.getRequestedChange({
      clientId,
      changeId
    });
    $[0] = changeId;
    $[1] = clientId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && changeId !== "";
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      throwOnError: false
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useMutationUpdateRequestedChange = t0 => {
  const $ = _c(3);
  const {
    clientId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== queryClient) {
    t1 = {
      mutationFn: creativeAssets.updateRequestedChange,
      onSuccess: async data => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.requestedChanges]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeMetadata, clientId, data.projectId]
        });
      }
    };
    $[0] = clientId;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useMutationDeleteRequestedChange = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: creativeAssets.deleteRequestedChange,
      onSuccess: async (_, variables) => {
        queryClient.removeQueries({
          queryKey: [queryKeys.requestedChanges, variables.clientId, variables.changeId.toString()]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.requestedChanges]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
type BulkCreateOrUpdateMetadataResult = Array<{
  creativeId: string;
  success: boolean;
  error?: fetch.ClientError;
}>;
interface BulkCreateOrUpdateMetadataVariables {
  creativeIds: string[];
  data: creativeAssets.CreateOrUpdateCreativeMetadataPayload;
}
export const useBulkMutationCreateOrUpdateCreativeMetadata = t0 => {
  const $ = _c(10);
  const {
    clientId,
    projectId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== projectId) {
    t1 = async t2 => {
      const {
        creativeIds,
        data
      } = t2;
      const createOrUpdatePromises = creativeIds.map(async creativeId => {
        ;
        try {
          await creativeAssets.createOrUpdateCreativeMetadata({
            clientId,
            projectId,
            creativeId,
            data
          });
          return {
            creativeId,
            success: true
          };
        } catch (t3) {
          const error = t3;
          return {
            creativeId,
            success: false,
            error: error as fetch.ClientError
          };
        }
      });
      return await Promise.all(createOrUpdatePromises);
    };
    $[0] = clientId;
    $[1] = projectId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== clientId || $[4] !== projectId || $[5] !== queryClient) {
    t2 = async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.creativeMetadata, clientId, projectId]
      });
    };
    $[3] = clientId;
    $[4] = projectId;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
};
async function _temp(t0) {
  const {
    project,
    clientId,
    projectId
  } = t0;
  return await projects.updateProject(clientId, projectId, project);
}
async function _temp2(t0) {
  const {
    projectId,
    clientId
  } = t0;
  await creativeAssets.deleteProjectMetadata({
    projectId,
    clientId
  });
}